import React from 'react';
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { isValidMetaPixelId, sanitizeMetaPixelId } from '../../features/meta-pixel';

export type MetaIntegrationInput = {
  pixelId?: string;
  enabled?: boolean;
  capiTokenConfigured?: boolean;
};

export function readMetaIntegration(input: MetaIntegrationInput) {
  const pixelOk = isValidMetaPixelId(sanitizeMetaPixelId(input.pixelId || ''));
  const enabledOk = pixelOk && input.enabled === true;
  const tokenOk = input.capiTokenConfigured === true;
  return {
    pixelOk,
    enabledOk,
    tokenOk,
    complete: enabledOk && tokenOk,
  };
}

type Props = {
  pixelId?: string;
  enabled?: boolean;
  capiTokenConfigured?: boolean;
  /** Aviso curto no topo do painel. Some quando a aba Marketing já está aberta. */
  variant?: 'banner' | 'panel';
  onOpenMarketing?: () => void;
};

const STEPS = [
  'Abra o Gerenciador de Eventos da Meta.',
  'Escolha o pixel deste evento (o mesmo número do Pixel ID).',
  'Clique em Configurações.',
  'Na seção API de Conversões, clique em Gerar token de acesso.',
  'Cole o token no campo abaixo e salve. Ele fica só neste evento e não aparece de novo na tela.',
];

const Row: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {ok ? (
      <CheckCircleOutlineIcon fontSize="small" color="success" />
    ) : (
      <RadioButtonUncheckedIcon fontSize="small" color="warning" />
    )}
    <Typography variant="body2">{label}</Typography>
  </Stack>
);

const MetaIntegrationNotice: React.FC<Props> = ({
  pixelId,
  enabled,
  capiTokenConfigured,
  variant = 'panel',
  onOpenMarketing,
}) => {
  const status = readMetaIntegration({ pixelId, enabled, capiTokenConfigured });

  if (variant === 'banner') {
    if (status.complete) return null;
    const missing = [
      !status.pixelOk ? 'Pixel ID' : null,
      status.pixelOk && !status.enabledOk ? 'ativar o rastreamento' : null,
      !status.tokenOk ? 'token da API de Conversões' : null,
    ].filter(Boolean);
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2, alignItems: 'center' }}
        action={
          onOpenMarketing ? (
            <Button color="inherit" size="small" onClick={onOpenMarketing} sx={{ whiteSpace: 'nowrap' }}>
              Completar
            </Button>
          ) : undefined
        }
      >
        Este evento ainda não está 100% ligado à Meta. Falta {missing.join(' e ')}. Sem isso, a
        campanha não enxerga as compras.
      </Alert>
    );
  }

  if (status.complete) {
    return (
      <Alert severity="success">
        Integração completa. As visitas e as compras confirmadas deste evento vão para o pixel da Meta.
      </Alert>
    );
  }

  return (
    <Alert severity="warning" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Este evento ainda não está 100% ligado à Meta
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 1.5 }}>
        <Row ok={status.pixelOk} label="Pixel ID preenchido" />
        <Row ok={status.enabledOk} label="Rastreamento ativado" />
        <Row ok={status.tokenOk} label="Token da API de Conversões salvo" />
      </Stack>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        O Pixel ID mostra a visita no navegador. O token é o que avisa a Meta quando o pagamento
        confirma, mesmo se a pessoa já fechou a página (Pix, boleto ou cartão). Sem o token, a
        campanha vê clique e quase nenhuma venda, e deixa de otimizar para quem compra.
      </Typography>
      {!status.tokenOk ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Como pegar o token
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.25, '& li': { mb: 0.35 } }}>
            {STEPS.map((step) => (
              <Typography key={step} component="li" variant="body2">
                {step}
              </Typography>
            ))}
          </Box>
          <Link
            href="https://business.facebook.com/events_manager"
            target="_blank"
            rel="noreferrer"
            variant="body2"
          >
            Abrir o Gerenciador de Eventos
          </Link>
        </Box>
      ) : null}
    </Alert>
  );
};

export default MetaIntegrationNotice;
